/**
 * @file fileNamePattern.js
 * @description Turns one example file name into a reusable format: a token
 *              template, an anchored regex, and enough metadata to render the
 *              name the next run should look for.
 *
 *              The inferred format is stored on the automation row, so the
 *              poller never re-infers — it just matches against the saved
 *              regex.
 *
 *              MIRRORED at server/common/fileNamePattern.js (CommonJS). That
 *              copy is generated from this one — edit here, regenerate there.
 */

// ── Small utilities ────────────────────────────────────────────────
const DELIMS = "_-. ";
const JS_DAY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export function escapeRe(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function isValidYmd(y, m, d) {
    if (y < 1990 || y > 2100) return false;
    if (m < 1 || m > 12) return false;
    if (d < 1 || d > 31) return false;
    const probe = new Date(y, m - 1, d);
    return probe.getMonth() === m - 1 && probe.getDate() === d;
}

function isValidTime(h, mi, s) {
    return h >= 0 && h <= 23 && mi >= 0 && mi <= 59 && (s === undefined || (s >= 0 && s <= 59));
}

/**
 * "YYYY-MM-DD" → "\d{4}-\d{2}-\d{2}"
 * "M-D-YY"     → "\d{1,2}-\d{1,2}-\d{2}"
 *
 * A single letter means the component isn't zero-padded, so it matches one
 * or two digits — that's what makes "8-3-26" and "12-25-26" both work off
 * the same pattern.
 */
export function fmtToRegex(fmt) {
    let out = "", i = 0;
    while (i < fmt.length) {
        const c = fmt[i];
        if (/[A-Za-z]/.test(c)) {
            let j = i;
            while (j < fmt.length && fmt[j] === c) j++;
            out += runToRegex(j - i);
            i = j;
        } else {
            out += escapeRe(c);
            i++;
        }
    }
    return out;
}

/** One letter → unpadded (1-2 digits). Otherwise a fixed width. */
function runToRegex(len) {
    return len === 1 ? "\\d{1,2}" : `\\d{${len}}`;
}

const pad = (n, w) => String(n).padStart(w, "0");

/** Fills YYYY / YY / MM / DD / HH / mm / ss from a Date. */
export function applyFmt(fmt, date) {
    // Longest tokens first, so MM never gets eaten by M.
    return fmt.replace(/YYYY|YY|MM|DD|HH|mm|ss|M|D|H/g, (t) => {
        switch (t) {
            case "YYYY": return String(date.getFullYear());
            case "YY":   return pad(date.getFullYear() % 100, 2);
            case "MM":   return pad(date.getMonth() + 1, 2);
            case "M":    return String(date.getMonth() + 1);
            case "DD":   return pad(date.getDate(), 2);
            case "D":    return String(date.getDate());
            case "HH":   return pad(date.getHours(), 2);
            case "H":    return String(date.getHours());
            case "mm":   return pad(date.getMinutes(), 2);
            case "ss":   return pad(date.getSeconds(), 2);
            default:     return t;
        }
    });
}

// ── Part splitting ─────────────────────────────────────────────────
/**
 * Walks the literal parts of a name and swaps matches for tokens.
 * `build(match, text)` returns a token or null to skip that match.
 */
function applyRule(parts, pattern, build, firstOnly) {
    let done = false;
    const out = [];

    for (const p of parts) {
        if (p.type !== "lit" || (firstOnly && done)) { out.push(p); continue; }

        const re = new RegExp(pattern.source, pattern.flags.includes("g") ? pattern.flags : pattern.flags + "g");
        const text = p.text;
        const pieces = [];
        let last = 0, m;

        while ((m = re.exec(text)) !== null) {
            const token = build(m, text);
            if (!token) {
                if (m.index === re.lastIndex) re.lastIndex++;
                continue;
            }
            pieces.push({ type: "lit", text: text.slice(last, m.index) });
            pieces.push(token);
            last = m.index + m[0].length;
            done = true;
            if (firstOnly) break;
            if (m.index === re.lastIndex) re.lastIndex++;
        }

        pieces.push({ type: "lit", text: text.slice(last) });
        out.push(...pieces.filter(x => x.type !== "lit" || x.text !== ""));
    }
    return out;
}

/** Whole-segment digit runs become counters; digits glued to letters stay literal. */
function applySeq(parts) {
    const out = [];
    for (const p of parts) {
        if (p.type !== "lit") { out.push(p); continue; }
        const segs = p.text.split(/([_\-. ])/);
        for (const seg of segs) {
            if (seg === "") continue;
            if (/^\d{1,4}$/.test(seg)) {
                out.push({ type: "seq", width: seg.length, value: Number(seg) });
            } else {
                const prev = out[out.length - 1];
                if (prev && prev.type === "lit") prev.text += seg;
                else out.push({ type: "lit", text: seg });
            }
        }
    }
    return out;
}

// ── Inference ──────────────────────────────────────────────────────
/**
 * @param {string} example  e.g. "VEROS_DPD30_20260803_01.csv"
 * @returns {{ok:boolean, error?:string, template?:string, regex?:string,
 *            extension?:string, tokens?:Array, example?:string}}
 */
export function inferFileFormat(example) {
    const raw = String(example || "").trim();
    if (!raw) return { ok: false, error: "Paste an example file name." };
    if (/[\\/]/.test(raw)) return { ok: false, error: "File name only — leave the folder path out." };

    const dot = raw.lastIndexOf(".");
    const hasExt = dot > 0 && dot < raw.length - 1;
    const extension = hasExt ? raw.slice(dot) : "";
    const base = hasExt ? raw.slice(0, dot) : raw;

    let parts = [{ type: "lit", text: base }];

    // 1. Delimited dates, longest and least ambiguous first.
    parts = applyRule(parts, /(\d{4})([-_.])(\d{2})\2(\d{2})/, (m) => (
        isValidYmd(+m[1], +m[3], +m[4])
            ? { type: "date", format: `YYYY${m[2]}MM${m[2]}DD` }
            : null
    ), true);

    parts = applyRule(parts, /(\d{2})([-_.])(\d{2})\2(\d{4})/, (m) => (
        isValidYmd(+m[4], +m[1], +m[3])
            ? { type: "date", format: `MM${m[2]}DD${m[2]}YYYY` }
            : null
    ), true);

    // 2. Unpadded dates: 8-3-26, 12-25-2026, 8.3.26. The component widths
    //    come from the example itself, so "8" yields M (1-2 digits) while
    //    "08" yields MM (exactly 2).
    parts = applyRule(parts, /(\d{1,2})([-_.])(\d{1,2})\2(\d{4}|\d{2})(?!\d)/, (m, text) => {
        if (m.index > 0 && /\d/.test(text[m.index - 1])) return null;
        const mo = +m[1], d = +m[3], y = m[4].length === 4 ? +m[4] : 2000 + +m[4];
        if (!isValidYmd(y, mo, d)) return null;
        const sep = m[2];
        // Two digits alone don't prove padding — 31 is simply two digits.
        // Only a leading zero does, so "7-31-26" reads as M-D-YY (rendering
        // Aug 4 as 8-4-26) while "07-31-26" reads as MM-DD-YY.
        const padded = (part) => part.length === 2 && part[0] === "0";
        return {
            type: "date",
            format: `${padded(m[1]) ? "MM" : "M"}${sep}`
                  + `${padded(m[3]) ? "DD" : "D"}${sep}`
                  + `${m[4].length === 4 ? "YYYY" : "YY"}`,
        };
    }, true);

    // 3. Solid 8-digit dates. Reject runs that are part of a longer number.
    parts = applyRule(parts, /\d{8}(?!\d)/, (m, text) => {
        if (m.index > 0 && /\d/.test(text[m.index - 1])) return null;
        const s = m[0];
        if (isValidYmd(+s.slice(0, 4), +s.slice(4, 6), +s.slice(6, 8)))
            return { type: "date", format: "YYYYMMDD" };
        if (isValidYmd(+s.slice(4, 8), +s.slice(0, 2), +s.slice(2, 4)))
            return { type: "date", format: "MMDDYYYY" };
        if (isValidYmd(+s.slice(4, 8), +s.slice(2, 4), +s.slice(0, 2)))
            return { type: "date", format: "DDMMYYYY" };
        return null;
    }, true);

    const hasDate = parts.some(p => p.type === "date");

    // 4. Solid 6-digit run: a short date if it reads as one, otherwise a time.
    parts = applyRule(parts, /\d{6}(?!\d)/, (m, text) => {
        if (m.index > 0 && /\d/.test(text[m.index - 1])) return null;
        const s = m[0];
        if (!hasDate && isValidYmd(2000 + +s.slice(0, 2), +s.slice(2, 4), +s.slice(4, 6)))
            return { type: "date", format: "YYMMDD" };
        if (isValidTime(+s.slice(0, 2), +s.slice(2, 4), +s.slice(4, 6)))
            return { type: "time", format: "HHmmss" };
        return null;
    }, true);

    // 5. A 4-digit time, but only once a date has anchored the name.
    if (hasDate) {
        parts = applyRule(parts, /\d{4}(?!\d)/, (m, text) => {
            if (m.index > 0 && /\d/.test(text[m.index - 1])) return null;
            const s = m[0];
            return isValidTime(+s.slice(0, 2), +s.slice(2, 4))
                ? { type: "time", format: "HHmm" }
                : null;
        }, true);
    }

    // 6. Everything left that stands alone between delimiters is a counter.
    parts = applySeq(parts);

    // ── Build template + regex ────────────────────────────────────
    let template = "", regex = "^", group = 0;
    const tokens = [];

    for (const p of parts) {
        if (p.type === "lit") {
            template += p.text;
            regex += escapeRe(p.text);
        } else if (p.type === "date" || p.type === "time") {
            group++;
            template += `{${p.format}}`;
            regex += `(${fmtToRegex(p.format)})`;
            tokens.push({ type: p.type, format: p.format, group });
        } else {
            group++;
            template += `{SEQ:${p.width}}`;
            regex += p.width === 1 ? "(\\d{1,3})" : `(\\d{${p.width}})`;
            tokens.push({ type: "seq", width: p.width, value: p.value, group });
        }
    }

    template += extension;
    regex += escapeRe(extension) + "$";

    if (!tokens.length) {
        // No variable part — treat it as a fixed name and say so.
        return {
            ok: true, fixed: true, example: raw, template, regex,
            extension, tokens: [],
        };
    }

    return { ok: true, fixed: false, example: raw, template, regex, extension, tokens };
}

// ── Matching / rendering ───────────────────────────────────────────
/** Case-insensitive by design — blob drops are inconsistent about casing. */
export function matchFileName(format, fileName) {
    if (!format?.regex) return { matched: false };
    let re;
    try { re = new RegExp(format.regex, "i"); } catch { return { matched: false }; }

    const m = re.exec(String(fileName || "").trim());
    if (!m) return { matched: false };

    const out = { matched: true, date: null, seq: null, time: null };
    for (const t of format.tokens || []) {
        const raw = m[t.group];
        if (raw == null) continue;
        if (t.type === "date") out.date = parseByFmt(t.format, raw);
        if (t.type === "time") out.time = raw;
        if (t.type === "seq")  out.seq  = Number(raw);
    }
    return out;
}

/**
 * Pulls Y/M/D back out of a matched string using its own format.
 * Builds a capture group per component rather than slicing at fixed
 * offsets, because "8-3-26" and "12-25-26" don't line up.
 */
export function parseByFmt(fmt, value) {
    const order = [];
    let re = "", i = 0;

    while (i < fmt.length) {
        const c = fmt[i];
        if (/[A-Za-z]/.test(c)) {
            let j = i;
            while (j < fmt.length && fmt[j] === c) j++;
            order.push({ letter: c, len: j - i });
            re += `(${runToRegex(j - i)})`;
            i = j;
        } else {
            re += escapeRe(c);
            i++;
        }
    }

    let m;
    try { m = new RegExp(`^${re}$`).exec(String(value)); } catch { return null; }
    if (!m) return null;

    let year = null, month = null, day = null;
    order.forEach((part, idx) => {
        const n = Number(m[idx + 1]);
        if (part.letter === "Y") year = part.len === 2 ? 2000 + n : n;
        if (part.letter === "M") month = n;
        if (part.letter === "D") day = n;
    });

    if (year == null || month == null || day == null) return null;
    return new Date(year, month - 1, day);
}

/**
 * Renders the name a run on `date` should produce.
 * @param {object} format  from inferFileFormat / the stored row
 * @param {Date}   date    run date
 * @param {object} opts    { seq, time } overrides
 */
export function renderFileName(format, date, opts = {}) {
    if (!format?.template) return "";
    const when = date instanceof Date ? date : new Date();

    let out = format.template;
    for (const t of format.tokens || []) {
        if (t.type === "date") {
            out = out.replace(`{${t.format}}`, applyFmt(t.format, when));
        } else if (t.type === "time") {
            const clock = opts.time || "00:00";
            const [hh, mi] = String(clock).split(":");
            const stamp = new Date(when);
            stamp.setHours(Number(hh) || 0, Number(mi) || 0, 0, 0);
            out = out.replace(`{${t.format}}`, applyFmt(t.format, stamp));
        } else {
            const value = opts.seq != null ? opts.seq : (t.value != null ? t.value : 1);
            out = out.replace(`{SEQ:${t.width}}`, pad(value, t.width));
        }
    }
    return out;
}

// ── Scheduling ─────────────────────────────────────────────────────
/**
 * The next moment this automation is watching for a file.
 * Today counts while its window is still open.
 * @returns {Date|null}
 */
export function nextRunAt(config, now = new Date()) {
    const days = config?.days || [];
    if (!days.length) return null;

    const [sh, sm] = String(config.startTime || "00:00").split(":").map(Number);
    const nowClock = `${pad(now.getHours(), 2)}:${pad(now.getMinutes(), 2)}`;

    for (let i = 0; i < 8; i++) {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i, sh || 0, sm || 0, 0, 0);
        if (!days.includes(JS_DAY[d.getDay()])) continue;
        if (i === 0 && nowClock > String(config.endTime || "23:59")) continue;
        return d;
    }
    return null;
}

/** Convenience: the file name the next run is waiting on. */
export function nextFileName(config, now = new Date()) {
    const format = toFormat(config);
    if (!format) return "";
    const when = nextRunAt(config, now) || now;
    return renderFileName(format, when, { time: config.startTime });
}

/** Normalizes a stored row into the shape the helpers expect. */
export function toFormat(config) {
    if (!config) return null;
    if (config.template && config.tokens) return config;
    if (!config.fileNameTemplate) return null;
    let tokens = [];
    try { tokens = JSON.parse(config.fileNameTokens || "[]"); } catch { tokens = []; }
    return {
        template: config.fileNameTemplate,
        regex: config.fileNamePattern,
        extension: config.fileExtension || "",
        tokens,
    };
}

/** Human-readable summary of what varies, for the dialog hint. */
export function describeFormat(format) {
    if (!format?.ok && !format?.template) return "";
    const tokens = format.tokens || [];
    if (!tokens.length) return "Fixed name — the file is expected to be named exactly this every run.";
    const bits = tokens.map(t => {
        if (t.type === "date") return `date as ${t.format}`;
        if (t.type === "time") return `time as ${t.format}`;
        return `a ${t.width}-digit counter`;
    });
    return `Varies by ${bits.join(" and ")}.`;
}